/**
 * Tenants SDK Client
 *
 * GraphQL client for the Tenants service.
 * Extends GraphQLClientBase for consistent retry and error handling.
 */

import { GraphQLClientBase, type GraphQLClientOptions } from "@eoln/graphql-client";
import type {
  CreateTenantInput,
  DeploymentCallbackResponse,
  ListTenantsOptions,
  ProvisioningResult,
  Tenant,
  TenantConnection,
  UpdateTenantInput,
  ValidationResult,
} from "../types/tenant.js";

/**
 * TenantsClient configuration options
 */
export interface TenantsClientOptions extends GraphQLClientOptions {}

/**
 * Client for interacting with the Tenants service API.
 *
 * Provides methods for tenant lifecycle management including:
 * - Tenant CRUD operations
 * - Provisioning and deployment
 * - Status management (suspend, activate, delete)
 *
 * @example
 * ```typescript
 * const client = new TenantsClient({
 *   endpoint: 'https://tenants.api.example.com/graphql',
 *   apiKey: 'sk_...'
 * });
 *
 * const tenant = await client.createTenant({
 *   tenantId: 'acme-corp',
 *   name: 'Acme Corporation',
 *   tier: 'PRO'
 * });
 * ```
 */
export class TenantsClient extends GraphQLClientBase {
  private readonly baseUrl: string;

  constructor(options: TenantsClientOptions) {
    if (!options.endpoint) {
      throw new Error("[TenantsClient Error]: Endpoint URL is required.");
    }
    super(options);
    // Extract base URL from endpoint (remove /graphql suffix if present)
    this.baseUrl = options.endpoint.replace(/\/graphql$/, "");
  }

  /**
   * Check if the tenants service is healthy
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/healthz`);
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Validate a tenant ID before creation.
   *
   * @param id - The tenant ID to validate
   * @returns Validation result with any errors
   */
  async validateTenantId(id: string): Promise<ValidationResult> {
    const query = `
      query ValidateTenantId($id: String!) {
        validateTenantId(id: $id) {
          valid
          errors
        }
      }
    `;
    const response = await this.graphqlRequest<{
      validateTenantId: ValidationResult;
    }>(query, { id });
    return response.validateTenantId;
  }

  /**
   * Create a new tenant.
   *
   * @param input - Tenant creation parameters
   * @returns Created tenant
   */
  async createTenant(input: CreateTenantInput): Promise<Tenant> {
    const query = `
      mutation CreateTenant($input: CreateTenantInput!) {
        createTenant(input: $input) {
          tenantId
          name
          status
          tier
          services {
            serviceName
            workerName
            status
          }
          customDomains {
            domain
            serviceName
            status
          }
          createdAt
          updatedAt
        }
      }
    `;
    const response = await this.graphqlRequest<{ createTenant: Tenant }>(query, { input });
    return response.createTenant;
  }

  /**
   * Get a tenant by ID.
   *
   * @param id - The tenant ID
   * @returns Tenant or null if not found
   */
  async getTenant(id: string): Promise<Tenant | null> {
    const query = `
      query GetTenant($id: ID!) {
        tenant(id: $id) {
          tenantId
          name
          status
          tier
          services {
            serviceName
            workerName
            status
            deployedAt
            version
            endpoints {
              default
              custom
            }
          }
          customDomains {
            domain
            serviceName
            status
            verificationTxt
          }
          createdAt
          updatedAt
        }
      }
    `;
    const response = await this.graphqlRequest<{ tenant: Tenant | null }>(query, { id });
    return response.tenant;
  }

  /**
   * List tenants with optional filtering and pagination.
   *
   * @param options - Filtering and pagination options
   * @returns Paginated tenant list
   */
  async listTenants(options?: ListTenantsOptions): Promise<TenantConnection> {
    const query = `
      query ListTenants($pagination: TenantPaginationInput) {
        tenants(pagination: $pagination) {
          nodes {
            tenantId
            name
            status
            tier
            createdAt
            updatedAt
          }
          totalCount
          hasMore
        }
      }
    `;
    const response = await this.graphqlRequest<{ tenants: TenantConnection }>(query, {
      pagination: {
        status: options?.status,
        limit: options?.limit ?? 50,
        offset: options?.offset ?? 0,
      },
    });
    return response.tenants;
  }

  /**
   * Update a tenant.
   *
   * @param id - The tenant ID
   * @param input - Update parameters
   * @returns Updated tenant
   */
  async updateTenant(id: string, input: UpdateTenantInput): Promise<Tenant> {
    const query = `
      mutation UpdateTenant($id: ID!, $input: UpdateTenantInput!) {
        updateTenant(id: $id, input: $input) {
          tenantId
          name
          status
          tier
          updatedAt
        }
      }
    `;
    const response = await this.graphqlRequest<{ updateTenant: Tenant }>(query, { id, input });
    return response.updateTenant;
  }

  /**
   * Suspend a tenant.
   *
   * @param id - The tenant ID
   * @param reason - Reason for suspension
   * @returns Updated tenant
   */
  async suspendTenant(id: string, reason: string): Promise<Tenant> {
    const query = `
      mutation SuspendTenant($id: ID!, $reason: String!) {
        suspendTenant(id: $id, reason: $reason) {
          tenantId
          status
        }
      }
    `;
    const response = await this.graphqlRequest<{ suspendTenant: Tenant }>(query, { id, reason });
    return response.suspendTenant;
  }

  /**
   * Deprovisions a tenant's infrastructure: deletes its Workers and D1
   * databases via the Cloudflare API, then removes the service records.
   *
   * This is NOT what `deleteTenant` does. `deleteTenant` only flips the tenant
   * row's status to "deleting" -- it never touches infrastructure. A tenant
   * that was provisioned and then merely deleted leaves its D1 databases
   * orphaned forever; 20 such databases had accumulated in dev by 2026-08-30.
   * Anything that provisions must deprovision.
   *
   * @param tenantId - The tenant ID to deprovision
   * @returns The tenant after deprovisioning
   */
  async deprovisionTenant(tenantId: string): Promise<Tenant> {
    const query = `
      mutation DeprovisionTenant($tenantId: ID!) {
        deprovisionTenant(tenantId: $tenantId) {
          tenantId
          status
        }
      }
    `;
    const response = await this.graphqlRequest<{ deprovisionTenant: Tenant }>(query, { tenantId });
    return response.deprovisionTenant;
  }

  /**
   * Provision a pending tenant.
   * Creates a PR for deployment and returns tenant with PENDING_DEPLOYMENT status.
   *
   * @param tenantId - The tenant ID to provision
   * @returns Provisioning result with tenant and optional PR info
   */
  async provisionTenant(tenantId: string): Promise<ProvisioningResult> {
    const query = `
      mutation ProvisionTenant($tenantId: ID!) {
        provisionTenant(tenantId: $tenantId) {
          tenantId
          status
          services {
            serviceName
            workerName
            status
          }
        }
      }
    `;
    const response = await this.graphqlRequest<{ provisionTenant: Tenant }>(query, { tenantId });
    return { tenant: response.provisionTenant };
  }

  /**
   * Call deployment completion callback endpoint.
   * This is called by CI/CD after tenant workers are deployed.
   *
   * @param tenantId - The tenant ID
   * @param provisionApiKey - API key with provisioning permissions
   * @returns Deployment callback response
   */
  async completeDeployment(tenantId: string, provisionApiKey: string): Promise<DeploymentCallbackResponse> {
    const response = await fetch(`${this.baseUrl}/api/provision/complete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": provisionApiKey,
      },
      body: JSON.stringify({ tenantId }),
    });

    if (!response.ok) {
      const error = (await response.json()) as { error?: string };
      throw new Error(error.error ?? `HTTP ${response.status}`);
    }

    return response.json() as Promise<DeploymentCallbackResponse>;
  }

  /**
   * Activate a suspended tenant.
   *
   * @param id - The tenant ID
   * @returns Updated tenant
   */
  async activateTenant(id: string): Promise<Tenant> {
    const query = `
      mutation ActivateTenant($id: ID!) {
        activateTenant(id: $id) {
          tenantId
          status
        }
      }
    `;
    const response = await this.graphqlRequest<{ activateTenant: Tenant }>(query, { id });
    return response.activateTenant;
  }

  /**
   * Delete a tenant (soft delete).
   *
   * @param tenantId - The tenant ID
   * @returns Deleted tenant
   */
  async deleteTenant(tenantId: string): Promise<Tenant> {
    const query = `
      mutation DeleteTenant($tenantId: ID!) {
        deleteTenant(tenantId: $tenantId) {
          tenantId
          status
        }
      }
    `;
    const response = await this.graphqlRequest<{ deleteTenant: Tenant }>(query, { tenantId });
    return response.deleteTenant;
  }
}
